import React, { type ComponentProps } from "react";
import { Button, Link, Text } from "./Common/spectrumCompat";

type SpectrumButtonVariant = ComponentProps<typeof Button>["variant"];

export type DxpPrimaryButtonProps = {
  label: string;
  onPress: () => void;
  /** Spectrum button variant; defaults to `accent` for primary actions. */
  variant?: SpectrumButtonVariant;
  /** Optional extra classes on the underlying Spectrum `Button`. */
  className?: string;
};

/** Primary / secondary action button used across former JSON-driven DXP confirmation pages. */
export function DxpPrimaryButton({
  label,
  onPress,
  variant = "accent",
  className,
}: DxpPrimaryButtonProps) {
  return (
    <Button variant={variant} onPress={onPress} UNSAFE_className={className}>
      {label}
    </Button>
  );
}

/** External / template link styled like DXP RichText links. */
export function DxpInlineLink({
  href,
  label,
  newTab,
}: {
  href: string;
  label: string;
  newTab?: boolean;
}) {
  return (
    <Link
      href={href}
      target={newTab ? "_blank" : "_self"}
      rel={newTab ? "noopener noreferrer" : undefined}
      UNSAFE_className="dxp-inline-link"
    >
      {label}
    </Link>
  );
}

/** White card container used on gray backgrounds. */
export function DxpMessageCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={["dxp-message-card", className].filter(Boolean).join(" ")}>
      {children}
    </div>
  );
}

/** Narrow column centered on the page (max-width 800px). */
export function DxpNarrowColumn({ children }: { children: React.ReactNode }) {
  return <div className="dxp-narrow-column">{children}</div>;
}

export function DxpHeading({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const headingClass =
    ["dxp-heading", className].filter(Boolean).join(" ") || undefined;

  return <div className={headingClass}>{children}</div>;
}

export function DxpBodyText({
  children,
  html,
  className,
}: {
  children?: React.ReactNode;
  html?: string;
  className?: string;
}) {
  if (html) {
    return (
      <div
        className={["dxp-body-text-html", className].filter(Boolean).join(" ")}
        // eslint-disable-next-line react/no-danger -- template copy from app logic
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  return (
    <Text
      UNSAFE_className={["dxp-body-text", className].filter(Boolean).join(" ")}
    >
      {children}
    </Text>
  );
}

export function DxpFooterRow({ children }: { children: React.ReactNode }) {
  return <div className="dxp-footer-row">{children}</div>;
}
